import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Package, Clock, CheckCircle, XCircle, MessageCircle, Send, Download, ArrowRight, Loader2, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusSteps = [
  { key: 'pending_approval', label: 'Por Aprobar' },
  { key: 'in_progress', label: 'En Proceso' },
  { key: 'completed', label: 'Completado' },
];

const ProgressBar = ({ status }) => {
  if (status === 'rejected' || status === 'cancelled') return null;
  const idx = statusSteps.findIndex(s => s.key === status);
  return (
    <div className="flex items-center gap-1 mt-3" data-testid="order-progress-bar">
      {statusSteps.map((step, i) => (
        <div key={step.key} className="flex items-center flex-1">
          <div className={`flex items-center gap-1.5 ${i <= idx ? 'text-emerald-600' : 'text-slate-300'}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 ${i <= idx ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300 text-slate-400'}`}>
              {i < idx ? <CheckCircle className="w-4 h-4" /> : i + 1}
            </div>
            <span className="text-xs font-medium hidden sm:inline">{step.label}</span>
          </div>
          {i < statusSteps.length - 1 && (
            <div className={`flex-1 h-0.5 mx-1 ${i < idx ? 'bg-emerald-500' : 'bg-slate-200'}`} />
          )}
        </div>
      ))}
    </div>
  );
};

const ChatPanel = ({ orderId, getAuthHeaders }) => {
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const loadMessages = async () => {
    try {
      const res = await axios.get(`${API}/client/orders/${orderId}/messages`, { headers: getAuthHeaders() });
      setMessages(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadMessages(); const iv = setInterval(loadMessages, 5000); return () => clearInterval(iv); }, [orderId]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async () => {
    if (!newMsg.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/client/orders/${orderId}/messages`, { message: newMsg }, { headers: getAuthHeaders() });
      setNewMsg('');
      loadMessages();
    } catch (e) { toast.error('Error al enviar'); }
    finally { setSending(false); }
  };

  return (
    <div className="mt-3 border rounded-xl overflow-hidden bg-slate-50" data-testid={`chat-panel-${orderId}`}>
      <div className="max-h-48 overflow-y-auto p-3 space-y-2">
        {messages.length === 0 && <p className="text-xs text-slate-400 text-center py-4">Sin mensajes. Escribe para consultar sobre tu orden.</p>}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.sender_role === 'cliente' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] px-3 py-2 rounded-xl text-sm ${m.sender_role === 'cliente' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-800 border'}`}>
              {m.sender_role === 'admin' && <p className="text-xs font-semibold text-blue-600 mb-0.5">Admin</p>}
              <p>{m.message}</p>
              <p className={`text-[10px] mt-1 ${m.sender_role === 'cliente' ? 'text-emerald-200' : 'text-slate-400'}`}>
                {new Date(m.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      <div className="flex gap-2 p-2 border-t bg-white">
        <Input value={newMsg} onChange={(e) => setNewMsg(e.target.value)} placeholder="Escribe un mensaje..." className="flex-1 h-9 text-sm" onKeyDown={(e) => e.key === 'Enter' && send()} data-testid={`chat-input-${orderId}`} />
        <Button size="sm" onClick={send} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700 h-9" data-testid={`chat-send-${orderId}`}>
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
};

const ClientOrdersPage = () => {
  const { getAuthHeaders } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedChat, setExpandedChat] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/client/orders`, { headers: getAuthHeaders() });
        setOrders(res.data);
      } catch (e) { console.error(e); } finally { setLoading(false); }
    };
    load();
  }, []);

  const downloadReceipt = async (orderId) => {
    try {
      const res = await axios.get(`${API}/client/orders/${orderId}/receipt`, {
        headers: getAuthHeaders(), responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `recibo_${orderId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) { toast.error('Error al descargar recibo'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;

  const statusConfig = {
    pending_approval: { icon: ShieldCheck, color: 'text-amber-500', bg: 'bg-amber-50 border-amber-200', label: 'Pendiente de Aprobacion' },
    pending: { icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-50 border-yellow-200', label: 'Pendiente' },
    in_progress: { icon: ArrowRight, color: 'text-blue-500', bg: 'bg-blue-50 border-blue-200', label: 'En Proceso' },
    completed: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50 border-green-200', label: 'Completado' },
    cancelled: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50 border-red-200', label: 'Cancelado' },
    rejected: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50 border-red-200', label: 'Rechazada - Reembolsada' },
  };

  return (
    <div className="space-y-6" data-testid="client-orders-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Mis Ordenes</h1>
        <p className="text-slate-500">Historial de tus servicios solicitados</p>
      </div>

      {orders.length === 0 ? (
        <Card className="p-12 text-center border-0 shadow-sm">
          <Package className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No tienes ordenes aun</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => {
            const sc = statusConfig[o.status] || statusConfig.pending;
            const Icon = sc.icon;
            const chatOpen = expandedChat === o.id;
            return (
              <Card key={o.id} className={`p-4 border ${sc.bg}`} data-testid={`order-${o.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`mt-1 ${sc.color}`}><Icon className="w-5 h-5" /></div>
                    <div>
                      <p className="font-semibold text-slate-900">{o.service_type}</p>
                      {o.details?.notes && <p className="text-sm text-slate-600 mt-1">{o.details.notes}</p>}
                      <p className="text-xs text-slate-500 mt-1">
                        {new Date(o.created_at).toLocaleString('es-ES')}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg text-slate-900">${o.amount?.toFixed(2)}</p>
                    <span className={`text-xs font-medium ${sc.color}`}>{sc.label}</span>
                    <div className="flex gap-1 mt-2">
                      <Button size="sm" variant="ghost" onClick={() => setExpandedChat(chatOpen ? null : o.id)} className="text-xs h-7 px-2" data-testid={`chat-toggle-${o.id}`}>
                        <MessageCircle className="w-3 h-3 mr-1" /> Chat
                      </Button>
                      {o.status === 'completed' && (
                        <Button size="sm" variant="ghost" onClick={() => downloadReceipt(o.id)} className="text-xs h-7 px-2 text-emerald-600" data-testid={`receipt-btn-${o.id}`}>
                          <Download className="w-3 h-3 mr-1" /> Recibo
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                {o.status !== 'cancelled' && o.status !== 'rejected' && <ProgressBar status={o.status} />}
                {o.status === 'rejected' && (
                  <div className="mt-3 flex items-center gap-2 text-red-600">
                    <XCircle className="w-4 h-4" />
                    <span className="text-sm">Orden rechazada - El monto fue reembolsado a tu wallet</span>
                  </div>
                )}
                {chatOpen && <ChatPanel orderId={o.id} getAuthHeaders={getAuthHeaders} />}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClientOrdersPage;
